/**
 * useLeaderboard
 *
 * Data for the Leaderboard page: company rankings + President's Club progress
 * (getLeaderboard, open to all CRM users) and the caller's private monthly
 * coaching review (getCoachingReview). Both callables live in us-east1.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/init-firebase';

const functions = getFunctions(app, 'us-east1');

export const LEADERBOARD_PERIODS = [
  { value: 'mtd', label: 'This Month' },
  { value: 'qtd', label: 'This Quarter' },
  { value: 'ytd', label: 'This Year' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all_time', label: 'All Time' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Completed YTD months as period options ({ value: 'YYYY-MM', label: 'January' }).
 * "This Month" already covers the current month, so this lists January through
 * last month of the current ET year (empty in January).
 */
export function getYtdMonthPeriods(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit',
  }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const year = Number(parts.year);
  const currentMonth = Number(parts.month);
  const options = [];
  for (let m = 1; m < currentMonth; m++) {
    options.push({ value: `${year}-${String(m).padStart(2, '0')}`, label: MONTH_NAMES[m - 1], year });
  }
  return options;
}

export function useLeaderboard() {
  const [period, setPeriod] = useState('mtd');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Private coaching review (separate loading state — it lazy-generates).
  const [review, setReview] = useState(null);
  const [reviewPeriod, setReviewPeriod] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const loadingRef = useRef(true);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const load = useCallback(async (p = period) => {
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'getLeaderboard');
      const res = await call({ period: p });
      setData(res.data);
    } catch (err) {
      console.error('getLeaderboard failed:', err);
      setError(err.message || 'Failed to load the leaderboard');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(period); }, [load, period]);

  // userId: admins may load another LO's review (backend enforces the gate).
  // The page drives WHEN to load — it re-fires this when its view-as user
  // selector changes, so there is deliberately no auto-load here.
  const loadReview = useCallback(async ({ generate = false, userId = null } = {}) => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      const call = httpsCallable(functions, 'getCoachingReview');
      const payload = {};
      if (generate) payload.generate = true;
      if (userId) payload.userId = userId;
      const res = await call(payload);
      setReview(res.data?.review || null);
      setReviewPeriod(res.data?.period || null);
      return { success: true, review: res.data?.review || null };
    } catch (err) {
      console.error('getCoachingReview failed:', err);
      const message = err.message || 'Failed to load your review';
      setReviewError(message);
      return { success: false, error: message };
    } finally {
      setReviewLoading(false);
    }
  }, []);

  return {
    period,
    setPeriod,
    data,
    loading,
    loadingRef,
    error,
    refresh: load,
    review,
    reviewPeriod,
    reviewLoading,
    reviewError,
    loadReview,
  };
}

export default useLeaderboard;
